import { useEffect, useRef, useState, type FormEvent, type Ref } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import Alert from "@mui/material/Alert";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { BrandLockup } from "../components/Brand";
import { VersionLine } from "../components/VersionLine";
import { config, flagEnabled } from "../config";
import { SUPPORT_EMAIL } from "../domain/contact";
import { loadMsg91Widget, WidgetError, type Msg91Widget } from "../auth/msg91Widget";
import { ForgotPassword } from "./ForgotPassword";
import { HumanCheck } from "../components/HumanCheck";
import { GoogleSignInButton, LegalConsentLine } from "../components/GoogleSignInButton";
import { TrustBrowserChoice } from "../components/TrustBrowserChoice";
import { rememberSignIn } from "../auth/signedInWith";
import {
  AUTH_REQUEST_ID,
  finishIdp,
  finishWithPassword,
  getAuthRequest,
  hostedLoginUrl,
  linkWithEmailCode,
  linkWithWidget,
  listIdps,
  maskMobile,
  isTestCodeRequest,
  normalizeIndianMobile,
  sendLoginCode,
  sendSecondCode,
  isRadcommEmailRequest,
  verifySecondEmailToken,
  sendTestCode,
  testCodeAccessToken,
  secondWithWidget,
  signInWithWidget,
  startEmail,
  startIdp,
  startLinkEmail,
  startOtp,
  startSecondEmail,
  submitPassword,
  verifyEmailCode,
  verifyOtp,
  verifySecondEmail,
  verifyTotp,
  type IdentityProvider,
  type LinkOutcome,
  type SecondFactorOption,
  type WidgetSignInOutcome,
} from "../api/login";
import { isTestMobile } from "../domain/testMobile";
import { CodeChannelChoice, codeChannels, codeSentLine, type CodeChannel } from "../components/CodeChannelChoice";
import { PasswordField } from "../components/PasswordField";

/**
 * The first-factor modes, then the second step: "choose" (SMS or email),
 * "second_sms", "second_email", and "finish_password" after a code.
 */
type Mode =
  | "mobile"
  | "code"
  | "password"
  | "totp"
  | "email"
  | "emailcode"
  | "choose"
  | "second_sms"
  | "second_email"
  | "finish_password"
  /** Forgotten password: its own steps, in ForgotPassword.tsx. */
  | "forgot";

/** The modes whose next call is a gated first step (auth/human.ts). */
/** The second-step screens, where "Trust this browser" is offered. */
const TRUST_MODES = new Set<string>(["totp", "choose", "second_sms", "second_email", "finish_password"]);

const SIGNIN_HUMAN_MODES: ReadonlySet<Mode> = new Set<Mode>(["mobile", "code", "password", "email", "choose"]);

/** The label of the one field that names the account on the password path. */
export const LOGIN_NAME_LABEL = "Username, email or mobile";

/** Everything the page can be told after a submit. */
type Notice =
  | WidgetSignInOutcome
  | { kind: "invalid_mobile" }
  | { kind: "invalid_email" }
  | { kind: "email_shared" }
  | { kind: "provider_email_unverified" }
  | { kind: "account_email_unverified" }
  | { kind: "link_refused" }
  | { kind: "idp_failed" }
  | { kind: "ticket_expired" }
  /** The ticket of a second step (authenticator, code or password) ran out. */
  | { kind: "step_expired" }
  | { kind: "widget_error"; message: string }
  | { kind: "resent"; channel: "sms" | "voice" }
  | { kind: "test_code" }
  | { kind: "test_refused"; message: string }
  /** A code from radaccess by SMS and/or email (owner 2026-10-25). */
  | { kind: "code_sent"; line: string; test: boolean };

/**
 * What must survive the round trip to Google and back: this browser started
 * THIS external sign-in for THIS auth request. sessionStorage, not a cookie:
 * it is per tab, it never reaches a server, and the ticket inside is sealed
 * and useless without the auth request it was sealed to.
 */
const IDP_HANDOFF = "ankhos.signin.idp";

interface IdpHandoff {
  authRequestId: string;
  ticket: string;
  provider: string;
}

function keepIdpHandoff(v: IdpHandoff) {
  try {
    window.sessionStorage.setItem(IDP_HANDOFF, JSON.stringify(v));
  } catch {
    /* a browser that refuses storage simply cannot finish this way */
  }
}

function takeIdpHandoff(): IdpHandoff | null {
  try {
    const raw = window.sessionStorage.getItem(IDP_HANDOFF);
    window.sessionStorage.removeItem(IDP_HANDOFF);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<IdpHandoff>;
    if (typeof v.authRequestId !== "string" || typeof v.ticket !== "string") return null;
    return {
      authRequestId: v.authRequestId,
      ticket: v.ticket,
      provider: typeof v.provider === "string" ? v.provider : "this sign-in",
    };
  } catch {
    return null;
  }
}

/** Seconds before the widget allows another send (MSG91 answered 704 before ~33 s). */
const WIDGET_RESEND_AFTER = 35;
const WIDGET_BUSY_RETRY = 60;

function widgetNotice(err: unknown): Notice {
  if (err instanceof WidgetError && err.throttled) return { kind: "busy", retryAfter: WIDGET_BUSY_RETRY };
  if (err instanceof WidgetError) return { kind: "widget_error", message: err.message };
  return { kind: "widget_error", message: "The SMS service could not be reached" };
}

/**
 * The sign-in form ZITADEL sends the browser to.
 *
 * 🔑 MOBILE NUMBER AND SMS CODE FIRST (owner decision 2026-09-11): in India
 * the mobile number is the identity -- Aadhaar-linked, portable, rarely
 * surrendered -- where email addresses change. Password is the second option,
 * one link away.
 *
 * 🔑 WHO SENDS THE CODE. With `msg91` in config.json (owner decision
 * 2026-09-13: no DLT template exists yet) MSG91's OTP widget sends and checks
 * the code in the browser, and radaccess confirms the widget's token with
 * MSG91 before signing in (POST /access/auth/widget). Without it, ZITADEL
 * sends the code (POST /access/auth/otp/start + verify). One form, two
 * deliveries; the answers after the code are the same.
 *
 * ⛔ PUBLIC, outside RequireAuth, and like /welcome it must never read
 * useIdentity or call a scoped gateway route. It reaches the gateway only on
 * the public sign-in paths in api/login.ts, all keyed by the auth request id
 * ZITADEL put in the URL.
 *
 * 🔑 THIS PAGE IS REACHED BY REDIRECT, NEVER BY A LINK. The app's ZITADEL
 * registration carries loginVersion.loginV2.baseUri = https://ankhos.io/auth,
 * so /oauth/v2/authorize 302s here with ?authRequest=V2_…. Without that id
 * there is nothing to sign in TO, which is why the page offers "start again"
 * (a fresh signinRedirect) rather than a form.
 *
 * 🔑 THE HOSTED LOGIN IS THE FALLBACK, NOT A COMPETITOR. Passkeys, a locked
 * account, password reset, and (Phase A) the social providers are all
 * finished on sso.ankhnet.io, for the SAME auth request. The backend says so
 * with a 409 and the URL; this page simply goes there.
 *
 * 🔑 AN AUTHENTICATOR APP IS ASKED FOR HERE (owner decision 2026-09-15). A
 * person who enrolled Google Authenticator gets `next: "totp"` after any
 * first factor; the form moves to the code step and radaccess checks the code
 * on the same ZITADEL session (POST /access/auth/totp/verify).
 *
 * 🔑 WHETHER A NUMBER HAS AN ACCOUNT. On the ZITADEL path the page cannot
 * tell and must not try: the backend answers "code sent" for every number.
 * On the widget path the backend says "no active account" only AFTER MSG91
 * has proved this person holds the number -- so it tells them about their
 * own number and nobody else's.
 *
 * 🪤 A silent-renew iframe lands here too (prompt=none). It must never see a
 * form: the backend answers such a request with an OAuth error callback, and
 * a framed render of this page is suppressed outright below.
 */
export function SignIn() {
  const auth = useAuth();
  const location = useLocation();
  const [params] = useSearchParams();
  // On the provider's return there is no authRequest in the URL: the handoff
  // this tab kept before leaving carries it.
  const [returned] = useState(() =>
    location.pathname.replace(/\/+$/, "").endsWith("/auth/idp") ? takeIdpHandoff() : null,
  );
  const authRequestId = params.get("authRequest") ?? returned?.authRequestId ?? "";

  const [hintLoaded, setHintLoaded] = useState(false);
  const [expired, setExpired] = useState(false);
  const [mode, setMode] = useState<Mode>("mobile");
  const [mobile, setMobile] = useState("");
  // Where the code goes (owner 2026-10-25): SMS, Email or Both; default SMS.
  const [channel, setChannel] = useState<CodeChannel>("sms");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [ticket, setTicket] = useState("");
  const [reqId, setReqId] = useState("");
  const [masked, setMasked] = useState("");
  const [resendUntil, setResendUntil] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const resendIn = Math.max(0, Math.ceil((resendUntil - now) / 1000));
  const setResendIn = (seconds: number) => {
    const t = Date.now();
    setNow(t);
    setResendUntil(t + seconds * 1000);
  };
  const [loginName, setLoginName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const totpRef = useRef<HTMLInputElement>(null);
  // The authenticator step: the sealed ticket carrying the first factor's
  // ZITADEL session, and the 6-digit code.
  const [totpTicket, setTotpTicket] = useState("");
  const [totpCode, setTotpCode] = useState("");
  // The second step of a two-step login: its sealed ticket (replaced by the
  // one /auth/second/email/start answers), the channels offered, and the
  // mobile the SMS one goes to (10 digits).
  const [secondTicket, setSecondTicket] = useState("");
  const [secondOptions, setSecondOptions] = useState<SecondFactorOption[]>([]);
  const [secondMobile, setSecondMobile] = useState("");
  const widgetRef = useRef<Msg91Widget | null>(null);
  // Providers (Google). linkTicket set = every proof below LINKS the provider
  // to the account it proves, instead of signing in with it.
  const [providers, setProviders] = useState<IdentityProvider[]>([]);
  const [linkTicket, setLinkTicket] = useState("");
  const [linkProvider, setLinkProvider] = useState("");
  const [finishing, setFinishing] = useState(() => returned !== null);
  const linking = linkTicket !== "";

  const framed = typeof window !== "undefined" && window.self !== window.top;
  const validId = AUTH_REQUEST_ID.test(authRequestId);
  const isLogout = location.pathname.replace(/\/+$/, "").endsWith("/logout");
  const hosted = validId ? hostedLoginUrl(config().oidc.authority, authRequestId) : "";
  const widgetCfg = config().msg91;

  // /auth/logout: with a per-app baseUri ZITADEL may send end_session here.
  // There is no browser session on our side to terminate (the backend's
  // sessions set no cookie), so forward to a same-origin target or home.
  useEffect(() => {
    if (!isLogout) return;
    const target = params.get("post_logout_redirect") ?? params.get("post_logout_redirect_uri") ?? "/welcome";
    let dest = "/welcome";
    try {
      const u = new URL(target, window.location.origin);
      if (u.origin === window.location.origin) dest = u.pathname + u.search;
    } catch {
      /* fall through to /welcome */
    }
    window.location.replace(dest);
  }, [isLogout, params]);

  useEffect(() => {
    if (!validId || framed || isLogout) return;
    const ac = new AbortController();
    getAuthRequest(authRequestId, ac.signal)
      .then((info) => {
        if (info === null) {
          setExpired(true);
        } else {
          // The welcome page puts the mobile number in login_hint. A hint
          // that is not a mobile (an old bookmark, another app) prefills the
          // password form's username instead.
          const asMobile = info.loginHint ? normalizeIndianMobile(info.loginHint) : null;
          if (asMobile) setMobile(asMobile.slice(3));
          else if (info.loginHint) setLoginName(info.loginHint);
          if (info.promptNone) setExpired(true);
        }
        setHintLoaded(true);
      })
      .catch(() => setHintLoaded(true));
    return () => ac.abort();
  }, [authRequestId, validId, framed, isLogout]);

  // The providers this deployment may offer, read once the auth request is
  // known. A failure here leaves the buttons off rather than showing one that
  // cannot work.
  useEffect(() => {
    if (!validId || framed || isLogout || !flagEnabled("signin.providers")) return;
    const ac = new AbortController();
    listIdps({ authRequestId }, ac.signal)
      .then((r) => setProviders(r.kind === "ok" ? r.providers : []))
      .catch(() => setProviders([]));
    return () => ac.abort();
  }, [authRequestId, validId, framed, isLogout]);

  // Coming back from the provider: finish the external sign-in once.
  useEffect(() => {
    if (!returned) return;
    const ac = new AbortController();
    const id = params.get("id") ?? "";
    const token = params.get("token") ?? "";
    if (params.get("result") === "failed" || !id || !token) {
      setNotice({ kind: "idp_failed" });
      setFinishing(false);
      return;
    }
    finishIdp({ authRequestId: returned.authRequestId, ticket: returned.ticket, intentId: id, intentToken: token }, ac.signal)
      .then((r) => {
        setFinishing(false);
        if (r.kind === "link") {
          setLinkTicket(r.linkTicket);
          setLinkProvider(r.provider);
          setMode("mobile");
          return;
        }
        if (r.kind === "totp") {
          setTotpTicket(r.ticket);
          setMode("totp");
          return;
        }
        if (r.kind === "password") {
          setSecondTicket(r.ticket);
          setMode("finish_password");
          return;
        }
        if (r.kind === "second_factor") {
          // Not expected after a provider; the choice screen handles one option too.
          setSecondTicket(r.ticket);
          setSecondOptions(r.options);
          setMode("choose");
          return;
        }
        if (r.kind === "expired" || r.kind === "ticket_expired") {
          setExpired(true);
          return;
        }
        setNotice(r as Notice);
      })
      .catch(() => {
        setFinishing(false);
        setNotice({ kind: "idp_failed" });
      });
    return () => ac.abort();
  }, [returned, params]);

  // Focus moves only when the form changes. loginName is read, not a
  // dependency: as one, every keystroke in the username field re-ran this and
  // pulled focus to the password field after the first character (owner,
  // test.ankhos.io 2026-09-15).
  useEffect(() => {
    if (mode === "code" || mode === "emailcode" || mode === "second_sms" || mode === "second_email") {
      codeRef.current?.focus();
    }
    if (mode === "totp") totpRef.current?.focus();
    if (mode === "finish_password") passwordRef.current?.focus();
    if (mode === "password" && loginName) passwordRef.current?.focus();
  }, [mode]);

  // Counted against a deadline, not by decrementing once a tick: a tab the
  // browser throttled in the background still reads the right number when
  // it wakes, instead of being stuck many seconds behind.
  useEffect(() => {
    if (resendUntil <= now) return;
    const t = window.setTimeout(() => setNow(Date.now()), 1000);
    return () => window.clearTimeout(t);
  }, [resendUntil, now]);

  // Navigation happens in an effect so the notice renders first: a user who
  // is being sent to the hosted page sees why for the instant it takes.
  useEffect(() => {
    if (!notice) return;
    if (notice.kind === "ok") window.location.assign(notice.callbackUrl);
    if (notice.kind === "fallback") window.location.assign(notice.hostedLoginUrl);
  }, [notice]);

  // Start the MSG91 widget as the page opens, not on Send. It fetches its own
  // settings (including whether a captcha must go with the send) after it
  // starts; loading it early means a person who types a number and presses
  // Send straight away does not race those settings. A failure here is
  // silent: Send loads it again and shows the error then.
  useEffect(() => {
    if (!widgetCfg || !validId || framed || isLogout) return;
    loadMsg91Widget(widgetCfg)
      .then((w) => {
        widgetRef.current = w;
      })
      .catch(() => undefined);
  }, [widgetCfg, validId, framed, isLogout]);

  if (framed || isLogout) return null;

  async function run(action: () => Promise<void>) {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    try {
      await action();
    } finally {
      setBusy(false);
    }
  }

  const backToMobile = () => {
    setTicket("");
    setReqId("");
    setCode("");
    setMode("mobile");
  };

  // A second step's ticket ran out: drop it and start the sign-in again.
  const stepExpired = () => {
    setSecondTicket("");
    setSecondOptions([]);
    setTotpTicket("");
    setTotpCode("");
    setCode("");
    setPassword("");
    setMode("mobile");
    setNotice({ kind: "step_expired" });
  };

  // The second step by SMS: radaccess makes the code and radcomm sends it to
  // the account's own phone (owner 2026-10-25); the MSG91 widget only when
  // radaccess cannot make codes.
  const sendSecondSms = async (mobile10: string, fromTicket: string) => {
    const e164 = normalizeIndianMobile(mobile10);
    if (e164 !== null) {
      const c = await sendSecondCode({ authRequestId, ticket: fromTicket });
      if (c.kind === "sent") {
        widgetRef.current = null;
        setSecondMobile(mobile10);
        setReqId(c.requestId);
        setMasked(maskMobile(e164));
        setResendIn(c.resendIn);
        setCode("");
        setMode("second_sms");
        return;
      }
      if (c.kind === "busy") {
        setNotice({ kind: "busy", retryAfter: c.retryAfter });
        setMode("choose");
        return;
      }
      if (c.kind === "refused") {
        setNotice({ kind: "unavailable", status: null });
        setMode("choose");
        return;
      }
    }
    if (!widgetCfg || e164 === null) {
      setNotice({ kind: "unavailable", status: null });
      return;
    }
    try {
      const widget = await loadMsg91Widget(widgetCfg);
      widgetRef.current = widget;
      const id = await widget.send(e164);
      setSecondMobile(mobile10);
      setReqId(id);
      setMasked(maskMobile(e164));
      setResendIn(WIDGET_RESEND_AFTER);
      setCode("");
      setMode("second_sms");
    } catch (err) {
      setNotice(widgetNotice(err));
      setMode("choose");
    }
  };

  // Both: ONE code by SMS and email (radaccess auth/second/code channel both);
  // the code box opens only after radaccess confirms the send.
  const sendSecondBoth = async (fromTicket: string) => {
    const c = await sendSecondCode({ authRequestId, ticket: fromTicket, channel: "both" });
    if (c.kind === "sent") {
      widgetRef.current = null;
      setSecondMobile(secondOptions.find((o) => o.method === "sms")?.mobile ?? "");
      setReqId(c.requestId);
      setMasked(c.email ? `your mobile and ${c.email}` : "your mobile and email");
      setResendIn(c.resendIn);
      setCode("");
      setMode("second_sms");
      return;
    }
    setNotice(c.kind === "busy" ? { kind: "busy", retryAfter: c.retryAfter } : { kind: "unavailable", status: null });
    setMode("choose");
  };

  // The second step by email: every start answers a NEW ticket.
  const sendSecondEmail = async (fromTicket: string) => {
    // radaccess makes the code and radcomm emails it (owner 2026-10-25:
    // ZITADEL's own email did not arrive); ZITADEL's only when radaccess
    // cannot make codes.
    const c = await sendSecondCode({ authRequestId, ticket: fromTicket, channel: "email" });
    if (c.kind === "sent") {
      setSecondTicket(fromTicket);
      setReqId(c.requestId);
      setMasked(c.email ?? "");
      setResendIn(c.resendIn);
      setCode("");
      setMode("second_email");
      return;
    }
    if (c.kind === "busy") {
      setNotice({ kind: "busy", retryAfter: c.retryAfter });
      return;
    }
    if (c.kind === "refused") {
      setNotice({ kind: "unavailable", status: null });
      return;
    }
    setReqId("");
    const r = await startSecondEmail({ authRequestId, ticket: fromTicket });
    if (r.kind === "sent") {
      setSecondTicket(r.ticket);
      setMasked(r.emailMasked);
      setResendIn(r.resendAfter);
      setCode("");
      setMode("second_email");
      return;
    }
    if (r.kind === "ticket_expired") {
      stepExpired();
      return;
    }
    if (r.kind === "expired") setExpired(true);
    setNotice(r);
  };

  // Which offered channels this page can run: SMS needs the widget.
  const usable = (options: SecondFactorOption[]) =>
    options.filter((o) => o.method === "email" || (o.method === "sms" && widgetCfg));

  const beginSecond = async (o: SecondFactorOption, fromTicket: string) => {
    setNotice(null);
    if (o.method === "sms") await sendSecondSms(o.mobile, fromTicket);
    else await sendSecondEmail(fromTicket);
  };

  // A step answered "something else is next" -- the authenticator app, a
  // second-step code, or the password: move to that step instead of showing
  // a notice. True when it did.
  const toNextStep = async (r: Notice): Promise<boolean> => {
    if (r.kind === "totp") {
      setTotpTicket(r.ticket);
      setTotpCode("");
      setNotice(null);
      setMode("totp");
      return true;
    }
    if (r.kind === "password") {
      setSecondTicket(r.ticket);
      setPassword("");
      setNotice(null);
      setMode("finish_password");
      return true;
    }
    if (r.kind !== "second_factor") return false;
    setSecondTicket(r.ticket);
    setSecondOptions(r.options);
    setPassword("");
    // Owner 2026-10-25: always show the choice, never send before the person picks.
    const can = usable(r.options);
    setNotice(can.length === 0 ? { kind: "unavailable", status: null } : null);
    setMode("choose");
    return true;
  };

  // A refusal that cannot be retried with another code ends the link attempt:
  // the ticket is dropped, so the next proof cannot silently link anything.
  const linkEnded = (r: Notice): boolean => {
    if (!linking) return false;
    if (r.kind !== "link_refused" && r.kind !== "account_email_unverified" && r.kind !== "ticket_expired") {
      return false;
    }
    setLinkTicket("");
    setCode("");
    setTicket("");
    setNotice(r);
    setMode("mobile");
    return true;
  };

  const signInWithProvider = (p: IdentityProvider) =>
    run(async () => {
      rememberSignIn("provider", p.name || "Google");
      const r = await startIdp({ authRequestId, idpId: p.id });
      if (r.kind === "redirect") {
        keepIdpHandoff({ authRequestId, ticket: r.ticket, provider: p.name });
        window.location.assign(r.authUrl);
        return;
      }
      if (r.kind === "expired") setExpired(true);
      else setNotice({ kind: "idp_failed" });
    });

  const sendWithWidget = async () => {
    if (!widgetCfg) return;
    const e164 = normalizeIndianMobile(mobile);
    if (e164 === null) {
      setNotice({ kind: "invalid_mobile" });
      return;
    }
    // ONE code from radaccess on the chosen channels (owner 2026-10-25). Only
    // when radaccess cannot make codes does the page fall back to the widget.
    // Linking a provider keeps the widget: its proof is the SMS itself.
    if (!linking) {
      const c = await sendLoginCode({ mobile: e164, channels: codeChannels(channel), purpose: "sign_in" });
      if (c.kind === "sent") {
        widgetRef.current = null;
        setReqId(c.requestId);
        setMasked(maskMobile(e164));
        setResendIn(c.resendIn);
        setCode("");
        setMode("code");
        setNotice({ kind: "code_sent", line: codeSentLine(maskMobile(e164), c.sms, c.email), test: isTestMobile(e164.slice(3)) });
        return;
      }
      if (c.kind === "busy") {
        setNotice({ kind: "busy", retryAfter: c.retryAfter });
        return;
      }
      if (c.kind === "refused") {
        setNotice({ kind: "test_refused", message: c.message });
        return;
      }
    }
    // A TEST number (test console only) cannot receive an SMS: radaccess
    // writes the code to the operator's SMS log instead (owner 2026-10-24).
    if (isTestMobile(e164.slice(3))) {
      const t = await sendTestCode(e164);
      if (t.kind === "sent") {
        widgetRef.current = null;
        setReqId(t.requestId);
        setMasked(maskMobile(e164));
        setResendIn(t.resendIn);
        setCode("");
        setMode("code");
        setNotice({ kind: "test_code" });
      } else if (t.kind === "busy") {
        setNotice({ kind: "busy", retryAfter: t.retryAfter });
      } else {
        setNotice({ kind: "test_refused", message: t.kind === "refused" ? t.message : "The code service could not be reached." });
      }
      return;
    }
    try {
      const widget = await loadMsg91Widget(widgetCfg);
      widgetRef.current = widget;
      const id = await widget.send(e164);
      setReqId(id);
      setMasked(maskMobile(e164));
      setResendIn(WIDGET_RESEND_AFTER);
      setCode("");
      setMode("code");
    } catch (err) {
      setNotice(widgetNotice(err));
    }
  };

  const sendCode = () =>
    run(async () => {
      if (widgetCfg) {
        await sendWithWidget();
        return;
      }
      const r = await startOtp({ authRequestId, mobile });
      if (r.kind === "sent") {
        setTicket(r.ticket);
        setMasked(r.mobileMasked);
        setResendIn(r.resendAfter);
        setCode("");
        setMode("code");
        return;
      }
      if (r.kind === "expired") setExpired(true);
      setNotice(r);
    });

  const resend = (channel: "sms" | "voice") =>
    run(async () => {
      if (mode !== "second_sms" && isTestCodeRequest(reqId)) {
        await sendWithWidget();
        return;
      }
      const widget = widgetRef.current;
      if (!widget || !reqId) {
        await (mode === "second_sms" ? sendSecondSms(secondMobile, secondTicket) : sendWithWidget());
        return;
      }
      try {
        await widget.resend(channel, reqId);
        setResendIn(WIDGET_RESEND_AFTER);
        setNotice({ kind: "resent", channel });
      } catch (err) {
        setNotice(widgetNotice(err));
      }
    });

  const onMobileSubmit = (e: FormEvent) => {
    e.preventDefault();
    void sendCode();
  };

  const verifyWithWidget = async () => {
    const widget = widgetRef.current;
    const e164 = normalizeIndianMobile(mobile);
    const testRequest = isTestCodeRequest(reqId);
    if (!reqId || e164 === null || (!widget && !testRequest)) {
      backToMobile();
      return;
    }
    let token: string;
    try {
      token = testRequest ? testCodeAccessToken(reqId, code) : await widget!.verify(code, reqId);
    } catch (err) {
      if (err instanceof WidgetError && err.throttled) {
        setNotice({ kind: "busy", retryAfter: WIDGET_BUSY_RETRY });
      } else {
        setNotice({ kind: "invalid" });
        setCode("");
      }
      return;
    }
    const r: WidgetSignInOutcome | LinkOutcome = linking
      ? await linkWithWidget({ authRequestId, linkTicket, mobile: e164, accessToken: token })
      : await signInWithWidget({ authRequestId, mobile: e164, accessToken: token });
    if (await toNextStep(r as Notice)) return;
    if (linkEnded(r as Notice)) return;
    setNotice(r as Notice);
    // A token is single-use: after any refusal of it, only a new code helps.
    if (r.kind === "token_rejected" || r.kind === "no_account" || r.kind === "invalid") backToMobile();
    if (r.kind === "expired") setExpired(true);
  };

  const onCodeSubmit = (e: FormEvent) => {
    e.preventDefault();
    rememberSignIn("sms", mobile);
    void run(async () => {
      if (widgetCfg) {
        await verifyWithWidget();
        return;
      }
      const r = await verifyOtp({ authRequestId, ticket, code });
      if (await toNextStep(r)) return;
      setNotice(r);
      if (r.kind === "invalid") setCode("");
      if (r.kind === "expired") setExpired(true);
      if (r.kind === "ticket_expired") {
        setTicket("");
        setMode("mobile");
      }
    });
  };

  // Sign-in by an emailed code (radaccess loginemail.go). Shares the code,
  // ticket and resend state with SMS: only one of the two is ever in flight.
  const sendEmailCode = () =>
    run(async () => {
      const r = linking
        ? await startLinkEmail({ authRequestId, linkTicket, email })
        : await startEmail({ authRequestId, email });
      if (r.kind === "sent") {
        setTicket(r.ticket);
        setMasked(r.emailMasked);
        setResendIn(r.resendAfter);
        setCode("");
        setMode("emailcode");
        return;
      }
      if (r.kind === "expired") setExpired(true);
      setNotice(r);
    });

  const onEmailSubmit = (e: FormEvent) => {
    e.preventDefault();
    void sendEmailCode();
  };

  const onEmailCodeSubmit = (e: FormEvent) => {
    e.preventDefault();
    rememberSignIn("email", email);
    void run(async () => {
      const r: LinkOutcome = linking
        ? await linkWithEmailCode({ authRequestId, linkTicket, ticket, code })
        : await verifyEmailCode({ authRequestId, ticket, code });
      if (await toNextStep(r as Notice)) return;
      if (linkEnded(r as Notice)) return;
      setNotice(r as Notice);
      if (r.kind === "invalid") setCode("");
      if (r.kind === "expired") setExpired(true);
      if (r.kind === "ticket_expired") {
        setTicket("");
        setMode("email");
      }
    });
  };

  const backToEmail = () => {
    setTicket("");
    setCode("");
    setMode("email");
  };

  const onPasswordSubmit = (e: FormEvent) => {
    e.preventDefault();
    rememberSignIn("password", loginName);
    void run(async () => {
      const r = await submitPassword({ authRequestId, loginName: loginName.trim(), password });
      if (await toNextStep(r)) {
        setPassword("");
        return;
      }
      setNotice(r);
      if (r.kind === "invalid" || r.kind === "unavailable") setPassword("");
      if (r.kind === "expired") setExpired(true);
    });
  };

  const onTotpSubmit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const r = await verifyTotp({ authRequestId, ticket: totpTicket, code: totpCode });
      if (r.kind === "totp") {
        // Asked for the same step twice: never loop on it.
        setNotice({ kind: "unavailable", status: 200 });
        return;
      }
      if (await toNextStep(r)) return;
      setNotice(r);
      if (r.kind === "invalid") setTotpCode("");
      if (r.kind === "expired") setExpired(true);
      if (r.kind === "ticket_expired") stepExpired();
    });
  };

  // The answers every second step shares after its own refusals.
  const afterSecond = async (r: Notice, clear: () => void) => {
    if (await toNextStep(r)) return;
    if (r.kind === "ticket_expired") {
      stepExpired();
      return;
    }
    setNotice(r);
    if (r.kind === "invalid") clear();
    if (r.kind === "expired") setExpired(true);
  };

  const onSecondSmsSubmit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const widget = widgetRef.current;
      const ours = isTestCodeRequest(reqId);
      if ((!widget && !ours) || !reqId) {
        setMode("choose");
        return;
      }
      let token: string;
      try {
        token = ours ? testCodeAccessToken(reqId, code) : await widget!.verify(code, reqId);
      } catch (err) {
        if (err instanceof WidgetError && err.throttled) {
          setNotice({ kind: "busy", retryAfter: WIDGET_BUSY_RETRY });
        } else {
          setNotice({ kind: "invalid" });
          setCode("");
        }
        return;
      }
      const r = await secondWithWidget({ authRequestId, ticket: secondTicket, accessToken: token });
      await afterSecond(r, () => setCode(""));
      // A token is single-use: after its refusal, only a new code helps.
      if (r.kind === "token_rejected") {
        setReqId("");
        setCode("");
      }
    });
  };

  const onSecondEmailSubmit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const r = isRadcommEmailRequest(reqId)
        ? await verifySecondEmailToken({ authRequestId, ticket: secondTicket, requestId: reqId, code })
        : await verifySecondEmail({ authRequestId, ticket: secondTicket, code });
      await afterSecond(r, () => setCode(""));
    });
  };

  const onFinishPasswordSubmit = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      const r = await finishWithPassword({ authRequestId, ticket: secondTicket, password });
      if (r.kind !== "ok") setPassword("");
      await afterSecond(r, () => setPassword(""));
    });
  };

  const startAgain = (
    <Button variant="contained" onClick={() => void auth.signinRedirect()}>
      Start sign-in again
    </Button>
  );

  const toPassword = (
    <Link component="button" type="button" onClick={() => { setNotice(null); setMode("password"); }}>
      Sign in with a password instead
    </Link>
  );

  // Back from any second step to the start, dropping its ticket.
  const startOver = (
    <Link
      component="button"
      type="button"
      onClick={() => {
        setNotice(null);
        setSecondTicket("");
        setSecondOptions([]);
        setCode("");
        setPassword("");
        setMode("mobile");
      }}
    >
      Start again
    </Link>
  );

  // Owner 2026-10-25: every sign-in step can go back to the full list of
  // ways in (SMS, email, password, Google), not only to one other way.
  const allWays = (
    <Link
      component="button"
      type="button"
      data-testid="all-ways"
      onClick={() => {
        setNotice(null);
        setPassword("");
        setCode("");
        setMode("mobile");
      }}
    >
      ← All sign-in options
    </Link>
  );

  // Off until radaccess serves /access/auth/email/* (flag signin.emailCode).
  const toEmail = flagEnabled("signin.emailCode") ? (
    <Link component="button" type="button" onClick={() => { setNotice(null); setMode("email"); }}>
      Sign in with an email code instead
    </Link>
  ) : null;

  // Shown while a provider is waiting to be linked. It says what will be
  // taken from the provider and why -- the DPDP notice at the moment of
  // linking (radius-wiki: Indian law table).
  const linkBanner = linking ? (
    <Alert severity="info" data-testid="link-banner">
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Prove this is your AnkhOS account</strong> and {linkProvider || "this sign-in"} will
        be added to it as another way to sign in. Nothing is added until you do.
      </Typography>
      <Typography variant="caption" color="text.secondary">
        We keep your name, email address and the provider's account id, so the provider can be
        recognised next time. It does not create an account, and we email you whenever a sign-in
        method is added. To remove one, contact{" "}
        <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>. See the{" "}
        <Link href="/legal/privacy">Privacy Policy</Link>.
      </Typography>
    </Alert>
  ) : null;

  const notices = (
    <>
      {notice?.kind === "invalid" && (
        <Alert severity="error">
          {mode === "totp"
            ? "That authenticator code is not right. Enter the code the app shows now."
            : mode === "code" || mode === "second_sms"
              ? "That code is not right. Check the SMS and try again."
              : mode === "emailcode" || mode === "second_email"
                ? "That code is not right. Check the email and try again."
                : mode === "finish_password"
                  ? "Wrong password."
                  : "Wrong username or password."}
        </Alert>
      )}
      {notice?.kind === "invalid_mobile" && (
        <Alert severity="error">Enter a 10-digit Indian mobile number.</Alert>
      )}
      {notice?.kind === "invalid_email" && (
        <Alert severity="error">Enter an email address, like name@example.com.</Alert>
      )}
      {notice?.kind === "idp_failed" && (
        <Alert severity="error">
          That sign-in did not complete. Try again, or sign in with your mobile number.
        </Alert>
      )}
      {notice?.kind === "provider_email_unverified" && (
        <Alert severity="error">
          {linkProvider || "That provider"} has not confirmed the email address on that account,
          so it cannot be linked. Confirm the address with the provider, or sign in with your
          mobile number.
        </Alert>
      )}
      {notice?.kind === "account_email_unverified" && (
        <Alert severity="error">
          Your AnkhOS account has no confirmed email address, and we must email you whenever a
          new sign-in method is added. Ask Ankh support to add and confirm your email address
          first.
        </Alert>
      )}
      {notice?.kind === "link_refused" && (
        <Alert severity="error">
          Could not link this sign-in. Sign in another way.
        </Alert>
      )}
      {notice?.kind === "email_shared" && (
        <Alert severity="error">
          More than one account uses this email address, so it cannot be used to sign in. Sign in
          with your mobile number or username instead. {toPassword}
        </Alert>
      )}
      {notice?.kind === "rate_limited" && (
        <Alert severity="warning">
          Too many attempts. Wait {Math.ceil(notice.retryAfter / 60)} minute
          {notice.retryAfter > 60 ? "s" : ""} and try again.
        </Alert>
      )}
      {notice?.kind === "busy" && (
        <Alert severity="warning">The SMS service is busy. Wait a minute and try again.</Alert>
      )}
      {notice?.kind === "widget_error" && (
        <Alert severity="error">
          The code could not be sent ({notice.message}). Try again, or {toPassword}.
        </Alert>
      )}
      {notice?.kind === "test_code" && (
        <Alert severity="info">
          Test number: no SMS is sent. Read the code in your operator's Reports › SMS log (status “TEST: not sent”).
        </Alert>
      )}
      {notice?.kind === "test_refused" && <Alert severity="error">{notice.message}</Alert>}
      {notice?.kind === "code_sent" && (
        <Alert severity="info">
          {notice.line}
          {notice.test && " Test number: no SMS is sent. Read the code in your operator's Reports › SMS log (status “TEST: not sent”)."}
        </Alert>
      )}
      {notice?.kind === "resent" && (
        <Alert severity="info">
          {notice.channel === "voice" ? "You will get a call with the code." : "A new code is on its way."}
        </Alert>
      )}
      {notice?.kind === "token_rejected" && (
        <Alert severity="error">That code could not be accepted. Send a new one.</Alert>
      )}
      {notice?.kind === "no_account" && (
        <Alert severity="warning">
          No active AnkhOS account uses this number. <Link href="/register">Register as a service provider</Link>,
          or write to <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>.
        </Alert>
      )}
      {notice?.kind === "ticket_expired" && (
        <Alert severity="info">That code has expired. Send a new one.</Alert>
      )}
      {notice?.kind === "step_expired" && (
        <Alert severity="info">That sign-in step timed out. Sign in again.</Alert>
      )}
      {notice?.kind === "locked" && (
        <Alert severity="error">
          This account is locked after too many attempts.{" "}
          <Link href={hosted}>Continue on the sign-in page</Link> to recover it.
        </Alert>
      )}
      {notice?.kind === "forbidden" && (
        <Alert severity="warning">
          You are signed in correctly, but this account has not been granted access to AnkhOS
          yet. Registration is reviewed before access is granted; write to{" "}
          <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link> if you think that review
          is complete.
        </Alert>
      )}
      {notice?.kind === "fallback" && <Alert severity="info">Continuing on the secure sign-in page…</Alert>}
      {notice?.kind === "ok" && <Alert severity="success">Signed in. Redirecting…</Alert>}
      {notice?.kind === "unavailable" && (
        <Alert severity="error">
          Sign-in could not be completed. You can <Link href={hosted}>use the sign-in page</Link>{" "}
          instead.
        </Alert>
      )}
    </>
  );

  let body;
  if (!validId || expired) {
    body = (
      <Stack spacing={2}>
        <Alert severity="info">
          {validId
            ? "This sign-in attempt has expired. Start again and it will take a few seconds."
            : "This page is reached from the Sign in button, not directly."}
        </Alert>
        {startAgain}
      </Stack>
    );
  } else if (finishing) {
    body = (
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Completing sign-in…</Typography>
      </Box>
    );
  } else if (!hintLoaded) {
    body = (
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Preparing sign-in…</Typography>
      </Box>
    );
  } else if (mode === "mobile") {
    body = (
      <form onSubmit={onMobileSubmit} noValidate>
        <Stack spacing={2}>
          {linkBanner}
          {notices}
          <TextField
            label="Registered mobile number"
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            autoComplete="tel-national"
            autoFocus
            required
            fullWidth
            slotProps={{
              input: { startAdornment: <Typography sx={{ mr: 1 }} color="text.secondary">+91</Typography> },
              htmlInput: { inputMode: "tel", "data-testid": "mobile" },
            }}
          />
          {!linking && <CodeChannelChoice value={channel} onChange={setChannel} disabled={busy} />}
          <Button type="submit" variant="contained" size="large" disabled={busy || !mobile.trim()}>
            {busy ? "Sending…" : "Send code"}
          </Button>
          <Typography variant="body2" color="text.secondary">
            {linking ? (
              <>We will send a one-time code by SMS to the number on your AnkhOS account.{" "}{toEmail}</>
            ) : (
              <>
                We will send a one-time code {channel === "both" ? "by SMS and email" : channel === "email" ? "by email" : "by SMS"}.
                {toEmail && <>{" "}{toEmail}</>}
              </>
            )}
          </Typography>
          {linking ? null : (
            <>
              <Divider>or</Divider>
              <Button
                variant="outlined"
                size="large"
                onClick={() => { setNotice(null); setMode("password"); }}
                data-testid="to-password"
              >
                Sign in with a password
              </Button>
            </>
          )}
          {linking ? null : providerBlock(providers, signInWithProvider, busy)}
          {linking ? null : socialBlock(hosted)}
        </Stack>
      </form>
    );
  } else if (mode === "code") {
    body = (
      <form onSubmit={onCodeSubmit} noValidate>
        <Stack spacing={2}>
          {linkBanner}
          <Typography variant="body2" data-testid="code-sent-to">
            {widgetCfg ? (
              <>A code is on its way to <strong>{masked}</strong> by SMS.</>
            ) : (
              <>If <strong>{masked}</strong> is registered, a code is on its way by SMS.</>
            )}
          </Typography>
          {notices}
          <TextField
            label="Code from the SMS"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 10))}
            autoComplete="one-time-code"
            inputRef={codeRef}
            required
            fullWidth
            slotProps={{ htmlInput: { inputMode: "numeric", "data-testid": "otp-code" } }}
          />
          <Button type="submit" variant="contained" size="large" disabled={busy || code.length < 4}>
            {busy ? "Checking…" : "Verify and sign in"}
          </Button>
          <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap" }}>
            <Button
              size="small"
              onClick={() => void (widgetCfg ? resend("sms") : sendCode())}
              disabled={busy || resendIn > 0}
            >
              {resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
            </Button>
            {widgetCfg && !isTestCodeRequest(reqId) && (
              <Button size="small" onClick={() => void resend("voice")} disabled={busy || resendIn > 0}>
                Call me instead
              </Button>
            )}
            <Button size="small" onClick={() => { setNotice(null); backToMobile(); }}>
              Change number
            </Button>
          </Stack>
        </Stack>
      </form>
    );
  } else if (mode === "email") {
    body = (
      <form onSubmit={onEmailSubmit} noValidate>
        <Stack spacing={2}>
          {linkBanner}
          {notices}
          <TextField
            label="Email address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoFocus
            required
            fullWidth
            slotProps={{ htmlInput: { inputMode: "email", "data-testid": "email" } }}
          />
          <Button type="submit" variant="contained" size="large" disabled={busy || !email.trim()}>
            {busy ? "Sending…" : "Send code"}
          </Button>
          <Typography variant="body2" color="text.secondary">
            We will email a one-time code to the verified address on your account.{" "}
            <Link component="button" type="button" onClick={() => { setNotice(null); setMode("mobile"); }}>
              Sign in with an SMS code instead
            </Link>
            {" · "}
            {toPassword}
          </Typography>
          <Typography variant="body2">{allWays}</Typography>
        </Stack>
      </form>
    );
  } else if (mode === "emailcode") {
    body = (
      <form onSubmit={onEmailCodeSubmit} noValidate>
        <Stack spacing={2}>
          {linkBanner}
          <Typography variant="body2" data-testid="email-code-sent-to">
            If <strong>{masked}</strong> is the verified address of an account, a code is on its way by
            email.
          </Typography>
          {notices}
          <TextField
            label="Code from the email"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 10))}
            autoComplete="one-time-code"
            inputRef={codeRef}
            required
            fullWidth
            slotProps={{ htmlInput: { inputMode: "numeric", "data-testid": "email-code" } }}
          />
          <Button type="submit" variant="contained" size="large" disabled={busy || code.length < 4}>
            {busy ? "Checking…" : "Verify and sign in"}
          </Button>
          <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap" }}>
            <Button size="small" onClick={() => void sendEmailCode()} disabled={busy || resendIn > 0}>
              {resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
            </Button>
            <Button size="small" onClick={() => { setNotice(null); backToEmail(); }}>
              Change email
            </Button>
          </Stack>
        </Stack>
      </form>
    );
  } else if (mode === "totp") {
    body = (
      <form onSubmit={onTotpSubmit} noValidate>
        <Stack spacing={2}>
          <Typography variant="body2" data-testid="totp-prompt">
            Open your authenticator app (for example Google Authenticator) and enter the 6-digit
            code it shows for AnkhOS.
          </Typography>
          {notices}
          <TextField
            label="Authenticator code"
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            autoComplete="one-time-code"
            inputRef={totpRef}
            required
            fullWidth
            slotProps={{ htmlInput: { inputMode: "numeric", "data-testid": "totp-code" } }}
          />
          <Button type="submit" variant="contained" size="large" disabled={busy || totpCode.length !== 6}>
            {busy ? "Checking…" : "Verify and sign in"}
          </Button>
          <Typography variant="body2" color="text.secondary">
            <Link
              component="button"
              type="button"
              onClick={() => {
                setNotice(null);
                setTotpTicket("");
                setTotpCode("");
                setMode("mobile");
              }}
            >
              Start again
            </Link>
          </Typography>
        </Stack>
      </form>
    );
  } else if (mode === "choose") {
    body = (
      <Stack spacing={2}>
        <ChooseChannelView
          options={usable(secondOptions)}
          busy={busy}
          onChoose={(o) => void run(() => beginSecond(o, secondTicket))}
          onBoth={() => void run(() => sendSecondBoth(secondTicket))}
        />
        {notices}
        <Typography variant="body2" color="text.secondary">{startOver}</Typography>
      </Stack>
    );
  } else if (mode === "second_sms" || mode === "second_email") {
    const sms = mode === "second_sms";
    const other = usable(secondOptions).find((o) => o.method === (sms ? "email" : "sms"));
    body = (
      <form onSubmit={sms ? onSecondSmsSubmit : onSecondEmailSubmit} noValidate>
        <Stack spacing={2}>
          <Typography variant="body2" data-testid="second-sent-to">
            One more step. A code is on its way to <strong>{masked}</strong> by {sms ? "SMS" : "email"}.
          </Typography>
          {notices}
          <TextField
            label={sms ? "Code from the SMS" : "Code from the email"}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 10))}
            autoComplete="one-time-code"
            inputRef={codeRef}
            required
            fullWidth
            slotProps={{ htmlInput: { inputMode: "numeric", "data-testid": "second-code" } }}
          />
          <Button type="submit" variant="contained" size="large" disabled={busy || code.length < 4}>
            {busy ? "Checking…" : "Verify and sign in"}
          </Button>
          <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap" }}>
            <Button
              size="small"
              onClick={() => void (sms ? resend("sms") : run(() => sendSecondEmail(secondTicket)))}
              disabled={busy || resendIn > 0}
            >
              {resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
            </Button>
            {other && (
              <Button size="small" disabled={busy} onClick={() => void run(() => beginSecond(other, secondTicket))}>
                {other.method === "sms" ? "Send code by SMS instead" : "Send code by email instead"}
              </Button>
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary">{startOver}</Typography>
        </Stack>
      </form>
    );
  } else if (mode === "forgot") {
    body = (
      <ForgotPassword
        authRequestId={authRequestId}
        widgetCfg={widgetCfg}
        onBack={() => { setNotice(null); setPassword(""); setMode("password"); }}
        onExpired={() => setExpired(true)}
      />
    );
  } else if (mode === "finish_password") {
    body = (
      <form onSubmit={onFinishPasswordSubmit} noValidate>
        <Stack spacing={2}>
          <Typography variant="body2" data-testid="finish-password-prompt">
            Enter your password to finish signing in.
          </Typography>
          {notices}
          <PasswordField
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            inputRef={passwordRef}
            required
            fullWidth
            slotProps={{ htmlInput: { "data-testid": "password" } }}
          />
          <Button type="submit" variant="contained" size="large" disabled={busy || !password}>
            {busy ? "Signing in…" : "Sign in"}
          </Button>
          <Typography variant="body2" color="text.secondary">
            {startOver}
            {" · "}
            <Link component="button" type="button" data-testid="forgot-password" onClick={() => { setNotice(null); setMode("forgot"); }}>
              Forgot password?
            </Link>
          </Typography>
        </Stack>
      </form>
    );
  } else {
    body = (
      <form onSubmit={onPasswordSubmit} noValidate>
        <Stack spacing={2}>
          {notices}
          <PasswordSignInFields
            loginName={loginName}
            onLoginName={setLoginName}
            password={password}
            onPassword={setPassword}
            passwordRef={passwordRef}
          />
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={busy || !loginName.trim() || !password}
          >
            {busy ? "Signing in…" : "Sign in"}
          </Button>
          <Typography variant="body2" color="text.secondary">
            <Link component="button" type="button" onClick={() => { setNotice(null); setMode("mobile"); }}>
              Sign in with an SMS code instead
            </Link>
            {toEmail && <>{" · "}{toEmail}</>}
            {" · "}
            <Link component="button" type="button" data-testid="forgot-password" onClick={() => { setNotice(null); setMode("forgot"); }}>
              Forgot password?
            </Link>
          </Typography>
          {/* Owner 2026-10-25: Google stays one click away from the password screen. */}
          {linking ? null : providerBlock(providers, signInWithProvider, busy)}
          <Typography variant="body2">{allWays}</Typography>
        </Stack>
      </form>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar>
          <BrandLockup to="/welcome" />
        </Toolbar>
      </AppBar>
      <Container maxWidth="xs" sx={{ py: { xs: 4, md: 8 } }}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Sign in to AnkhOS
          </Typography>
          {body}
          {/* Owner 2026-10-25: a second step done here can be remembered on this browser. */}
          {TRUST_MODES.has(mode) && <TrustBrowserChoice />}
          {/* The human check (owner 2026-10-24) on the modes whose next call is a
              gated first step: password, SMS (widget or ZITADEL), email, Google. */}
          {SIGNIN_HUMAN_MODES.has(mode) && (
            <Box sx={{ mt: 2 }}>
              <HumanCheck action="signin" />
            </Box>
          )}
        </Paper>
        <VersionLine />
      </Container>
    </Box>
  );
}

/**
 * The account and password fields of the password path. The one name field
 * takes a username, an email or a mobile: radaccess resolves which.
 */
export function PasswordSignInFields(props: {
  loginName: string;
  onLoginName: (v: string) => void;
  password: string;
  onPassword: (v: string) => void;
  passwordRef?: Ref<HTMLInputElement>;
}) {
  return (
    <>
      <TextField
        label={LOGIN_NAME_LABEL}
        value={props.loginName}
        onChange={(e) => props.onLoginName(e.target.value)}
        autoComplete="username"
        autoFocus={!props.loginName}
        required
        fullWidth
        slotProps={{ htmlInput: { "data-testid": "login-name" } }}
      />
      <PasswordField
        label="Password"
        value={props.password}
        onChange={(e) => props.onPassword(e.target.value)}
        autoComplete="current-password"
        inputRef={props.passwordRef}
        required
        fullWidth
        slotProps={{ htmlInput: { "data-testid": "password" } }}
      />
    </>
  );
}

/** The second step's choice: one button per channel this page can use. */
export function ChooseChannelView(props: {
  options: SecondFactorOption[];
  busy: boolean;
  onChoose: (o: SecondFactorOption) => void;
  /** Owner 2026-10-25: SMS, Email or Both -- one code to both. */
  onBoth?: () => void;
}) {
  const both = props.onBoth && props.options.some((o) => o.method === "sms") && props.options.some((o) => o.method === "email");
  return (
    <>
      <Typography variant="body2" data-testid="choose-channel">
        One more step. Where should we send your code?
      </Typography>
      {props.options.map((o) => (
        <Button
          key={o.method}
          variant="outlined"
          size="large"
          disabled={props.busy}
          onClick={() => props.onChoose(o)}
          data-testid={`second-${o.method}`}
        >
          {o.method === "sms" ? "Send code by SMS" : "Send code by email"}
          {o.masked ? ` to ${o.masked}` : ""}
        </Button>
      ))}
      {both && (
        <Button variant="outlined" size="large" disabled={props.busy} onClick={props.onBoth} data-testid="second-both">
          Send code by SMS and email
        </Button>
      )}
    </>
  );
}

/**
 * Phase A social sign-in: the buttons go to the hosted page with this auth
 * request, where the providers are offered. No flag, no buttons.
 *
 * 🔑 Linking-only: a Google or Facebook account signs in an EXISTING,
 * approved operator user whose mobile number is already verified; it never
 * creates one. The caption is the DPDP Act notice at the moment of linking.
 */
/**
 * The providers radaccess says are active (flag signin.providers). Each button
 * starts an intent and hands the browser to the provider; the return lands on
 * /auth/idp, which this page finishes.
 */
function providerBlock(
  providers: IdentityProvider[],
  start: (p: IdentityProvider) => void,
  busy: boolean,
) {
  if (!flagEnabled("signin.providers") || providers.length === 0) return null;
  return (
    <>
      <Divider>or</Divider>
      {providers.map((p) =>
        isGoogle(p) ? (
          <GoogleSignInButton key={p.id} disabled={busy} onClick={() => start(p)} testId={`provider-${p.id}`} />
        ) : (
          <Button key={p.id} variant="outlined" disabled={busy} onClick={() => start(p)} data-testid={`provider-${p.id}`}>
            Continue with {p.name}
          </Button>
        ),
      )}
      <Typography variant="caption" color="text.secondary">
        You can sign in this way once it is added to an existing AnkhOS account. The first time,
        we ask you to prove the account by a code. It never creates an account.
      </Typography>
      <LegalConsentLine />
    </>
  );
}

const isGoogle = (p: IdentityProvider) => p.type === "IDENTITY_PROVIDER_TYPE_GOOGLE" || p.name.toLowerCase() === "google";

function socialBlock(hosted: string) {
  if (!flagEnabled("login.social")) return null;
  return (
    <>
      <Divider>or</Divider>
      <Button variant="outlined" href={hosted} data-testid="social-google">
        Continue with Google
      </Button>
      <Button variant="outlined" href={hosted} data-testid="social-facebook">
        Continue with Facebook
      </Button>
      <Typography variant="caption" color="text.secondary">
        Signing in with Google or Facebook shares your name, email address and profile picture
        with AnkhOS to match an existing, approved operator account. It does not create an
        account. See the <Link href="/legal/privacy">Privacy Policy</Link>.
      </Typography>
    </>
  );
}
